import {define, inject} from '@injex/core';
import {makeObservable, observable} from 'mobx';
import {IScheduleReport} from '../../interfaces/IScheduleReport';
import {ColumnsManager} from '@vidazoo/ui-kit';
import ApiManager from '../../../../common/api/ApiManager.mdl';
import {DataListQuery} from '../../../../common/models/DataListQuery';
import {ScheduleReportsTableColumns} from '../common/constants';

@define()
export class ScheduleReportsListModel extends ColumnsManager<IScheduleReport> {

    @inject() private apiManager: ApiManager;
    public items: DataListQuery<IScheduleReport, []>;
    @observable public pendingIds: string[] = [];
    @observable public actionError = '';
    @observable public promptDeleteItem: IScheduleReport = null;

    constructor() {
        super({
            persistKey: 'schedule_reports_list_classic_columns',
        });

        this.setColumns(ScheduleReportsTableColumns);

        this.items = new DataListQuery(this._fetchItems.bind(this), {defaultValue: [], dataObservType: observable.ref});

        makeObservable(this);

    }

    public get data(): IScheduleReport[] {
        return this.items.data || [];
    }

    public get isLoading(): boolean {
        return this.items.isLoading;
    }

    private async _fetchItems() {
        const response = await this.apiManager.scheduleReports.getAll({page: 1, pageSize: 10000,
            fields: {name: 1, schedule: 1, dateRange: 1, lastSchedule: 1, nextSchedule: 1, created: 1, isActive: 1, errorCount: 1},
            sort: {name: 1}}, false);
        return response.results;
    }

    public fetch() {
        return this.items.invalidate().fetch();
    }

    public toggleActive = async (item: IScheduleReport) => {
        if (this.pendingIds.includes(item._id)) return;
        this.pendingIds = [...this.pendingIds, item._id];
        this.actionError = '';
        try {
            const active = !item.isActive;
            await this.apiManager.scheduleReports.activeById(item._id, active);
            this.items.data = this.data.map(row => row._id === item._id ? {...row, isActive: active} : row);
        } catch {
            this.actionError = 'Failed to update schedule report. Please try again.';
        } finally {
            this.pendingIds = this.pendingIds.filter(id => id !== item._id);
        }
    };

    public deleteItem = async () => {
        const item = this.promptDeleteItem;
        this.promptDeleteItem = null;
        if (!item || this.pendingIds.includes(item._id)) return;
        this.pendingIds = [...this.pendingIds, item._id];
        this.actionError = '';
        try {
            await this.apiManager.scheduleReports.deleteById(item._id);
            this.items.data = this.data.filter(row => row._id !== item._id);
        } catch {
            this.actionError = 'Failed to delete schedule report. Please try again.';
        } finally {
            this.pendingIds = this.pendingIds.filter(id => id !== item._id);
        }
    };
}
